import React, { ComponentProps, MouseEvent } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useSelector } from '@xstate/react';
import { FormattedMessage } from '@kyruus/intl';
import { PMCConfig } from '@kyruus/types';

import type {
  AvailabilityProps,
  Log,
  SearchSummary,
  SortOption
} from 'Src/types';
import {
  getDefaultCustomerSort,
  getSortOptions
} from 'Src/utils/search-common';
import { getProvidersAtLocationUrl } from 'Src/utils/url';
import { EVENTS } from 'Src/machines/location-v9/types';
import type { LocationActorRef } from 'Src/machines/location-v9';
import { LOGGING_EVENTS } from '../constants';
import { ContentSection, SectionHeader } from '../styles';
import messages from '../messages';
import { FindProvidersButtonLink } from './FindProvidersButtonLink';
import { ProvidersList } from './ProvidersList';
import { ResultsHeader } from 'Src/shared/ResultsHeader';

interface BaseProvidersProps {
  config: PMCConfig;
  customerCode: string;
  locationId: string;
  log: Log;
  productName: string;
  searchSummary: SearchSummary;
  service: LocationActorRef;
  titleId: string;
}

type ProvidersProps = BaseProvidersProps &
  ComponentProps<'section'> &
  AvailabilityProps;

export const Providers = ({
  config,
  customerCode,
  locationId,
  log,
  productName,
  searchSummary,
  service,
  appointmentInfo,
  slots,
  slotsStatus,
  purposeOptionsByProviderId,
  availabilityError,
  purposesLoading,
  fetchProviderAvailability,
  fetchPurposesByProviderId,
  titleId,
  ...props
}: ProvidersProps) => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const state = useSelector(service, (snapshot) => snapshot);

  const defaultSort = getDefaultCustomerSort(config);
  const findProvidersLink = getProvidersAtLocationUrl(id, history, defaultSort);

  const handleFindProvidersClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    log(LOGGING_EVENTS.CLICK_FIND_PROVIDERS);
    history.push(findProvidersLink);
  };

  const { totalProviders = 0 } = state.context;

  const sortOptionsWithoutDistance: SortOption[] = getSortOptions(
    config,
    state.context.currentQuery
  ).filter((item: SortOption) => item.descriptor_id !== 'distance');

  const handleSortChange = (sort: string) => {
    log(LOGGING_EVENTS.CLICK_SORT_PROVIDERS, { sort });
    service.send({
      type: EVENTS.newProvidersSearch,
      data: {
        currentQuery: {
          ...state.context.currentQuery,
          sort
        }
      }
    });
  };

  return (
    <ContentSection
      data-testid="location-providers"
      aria-labelledby={titleId}
      {...props}
    >
      <FormattedMessage
        {...messages.providersTitle}
        values={{ count: totalProviders }}
      >
        {(text) => (
          <SectionHeader id={titleId} data-testid={titleId}>
            {text}
          </SectionHeader>
        )}
      </FormattedMessage>

      <ResultsHeader
        config={config}
        onSortChange={handleSortChange}
        sort={state.context.currentQuery.sort}
        sortOptions={sortOptionsWithoutDistance}
        heading={
          <FormattedMessage {...messages.findProvidersAtLocationButtonLabel}>
            {(text) => (
              <Link
                to={findProvidersLink}
                onClick={handleFindProvidersClick}
                component={FindProvidersButtonLink}
              >
                {text}
              </Link>
            )}
          </FormattedMessage>
        }
        log={log}
        sx={{ mb: 2 }}
      />

      {totalProviders > 0 && (
        <ProvidersList
          config={config}
          customerCode={customerCode}
          locationId={locationId}
          log={log}
          productName={productName}
          searchSummary={searchSummary}
          service={service}
          appointmentInfo={appointmentInfo}
          slots={slots}
          slotsStatus={slotsStatus}
          purposeOptionsByProviderId={purposeOptionsByProviderId}
          availabilityError={availabilityError}
          purposesLoading={purposesLoading}
          fetchProviderAvailability={fetchProviderAvailability}
          fetchPurposesByProviderId={fetchPurposesByProviderId}
        />
      )}
    </ContentSection>
  );
};
